import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AsyncBoundary, useToast } from '@kollez/design-system';
import { apiGet, apiPatch } from '@/lib/api';

type TenantKind = 'PLATFORM' | 'GROUP' | 'UNIVERSITY' | 'COLLEGE' | 'DEPARTMENT';

const TITLE: Record<TenantKind, string> = {
  PLATFORM: 'Platform',
  GROUP: 'Group',
  UNIVERSITY: 'University',
  COLLEGE: 'College',
  DEPARTMENT: 'Department',
};
const SUBHEAD: Record<TenantKind, string> = {
  PLATFORM: 'Platform-wide defaults. Individual tenants can tighten these, not relax them.',
  GROUP: 'Policy that applies to every institution under this group. Institutions can tighten these, not relax them.',
  UNIVERSITY: 'Policy that applies to every college affiliated with this university. Colleges can tighten these, not relax them.',
  COLLEGE: 'Policy for this college and every department under it. Departments can tighten these, not relax them.',
  DEPARTMENT: 'Policy for this department.',
};

interface GroupSettings {
  _id: string;
  name: string;
  tenantType?: TenantKind;
  primaryColor?: string;
  settings?: {
    allowStudentRegistration?: boolean;
    requireEmailVerification?: boolean;
    allowPublicOpportunities?: boolean;
    timezone?: string;
    language?: 'en' | 'te' | 'hi';
  };
  placementPolicy?: {
    day0ThresholdInr?: number;
    day1ThresholdInr?: number;
    offersPerStudent?: 'one_per_tier' | 'one_total' | 'unlimited';
    sitOutRule?: 'same_or_lower_tier' | 'none';
  };
}

/** /group/settings — placement policy + data + branding. Reads and writes /group/settings. */
export function GroupSettingsPage() {
  const toast = useToast();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['group', 'settings'],
    queryFn: () => apiGet<{ settings: GroupSettings }>('/group/settings'),
  });

  // Local draft state so users can edit without every keystroke firing a save.
  const [draft, setDraft] = useState<Partial<GroupSettings>>({});
  useEffect(() => {
    if (query.data?.settings) setDraft(query.data.settings);
  }, [query.data?.settings]);

  const save = useMutation({
    mutationFn: (patch: Partial<GroupSettings>) => apiPatch<{ settings: GroupSettings }>('/group/settings', patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['group', 'settings'] }),
  });

  const doSave = (label: string, patch: Partial<GroupSettings>) => {
    save.mutate(patch, {
      onSuccess: () => toast.show({ message: `${label} saved. Changes take effect from the next drive.` }),
      onError: (e) => toast.show({ tone: 'warning', message: (e as Error)?.message ?? 'Save failed' }),
    });
  };

  const kind = query.data?.settings?.tenantType ?? 'GROUP';
  const kindLabel = TITLE[kind];
  const subhead = SUBHEAD[kind];

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Administration</p>
          <h1 className="h-page">{kindLabel} settings</h1>
          <p className="subhead">{subhead}</p>
        </div>
      </div>

      <AsyncBoundary kind="detail" loading={query.isLoading} error={query.error}>
        <section className="card card-pad grid gap-4">
          <h2 className="h-section">Placement policy</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="grid gap-1.5">
              <span className="label">Day 0 threshold</span>
              <div className="input-group">
                <span className="input-addon">₹</span>
                <input
                  className="input"
                  type="number"
                  value={String(draft.placementPolicy?.day0ThresholdInr ?? 900000)}
                  onChange={(e) => setDraft((d) => ({ ...d, placementPolicy: { ...d.placementPolicy, day0ThresholdInr: Number(e.target.value) } }))}
                />
              </div>
              <span className="helper">CTC at or above this is Day 0</span>
            </label>
            <label className="grid gap-1.5">
              <span className="label">Day 1 threshold</span>
              <div className="input-group">
                <span className="input-addon">₹</span>
                <input
                  className="input"
                  type="number"
                  value={String(draft.placementPolicy?.day1ThresholdInr ?? 450000)}
                  onChange={(e) => setDraft((d) => ({ ...d, placementPolicy: { ...d.placementPolicy, day1ThresholdInr: Number(e.target.value) } }))}
                />
              </div>
            </label>
            <label className="grid gap-1.5">
              <span className="label">Offers per student</span>
              <select
                className="select"
                value={draft.placementPolicy?.offersPerStudent ?? 'one_per_tier'}
                onChange={(e) => setDraft((d) => ({ ...d, placementPolicy: { ...d.placementPolicy, offersPerStudent: e.target.value as NonNullable<GroupSettings['placementPolicy']>['offersPerStudent'] } }))}
              >
                <option value="one_per_tier">One per tier · may move up once</option>
                <option value="one_total">One total</option>
                <option value="unlimited">Unlimited</option>
              </select>
            </label>
            <label className="grid gap-1.5">
              <span className="label">Sit-out rule</span>
              <select
                className="select"
                value={draft.placementPolicy?.sitOutRule ?? 'same_or_lower_tier'}
                onChange={(e) => setDraft((d) => ({ ...d, placementPolicy: { ...d.placementPolicy, sitOutRule: e.target.value as NonNullable<GroupSettings['placementPolicy']>['sitOutRule'] } }))}
              >
                <option value="same_or_lower_tier">Placed students can't apply to same or lower tier</option>
                <option value="none">No sit-out</option>
              </select>
            </label>
          </div>
          <button
            className="btn btn-primary justify-self-start"
            disabled={save.isPending}
            onClick={() => doSave('Placement policy', { placementPolicy: draft.placementPolicy })}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </section>

        <section className="card card-pad grid gap-4">
          <h2 className="h-section">Data &amp; access</h2>
          <label className="flex items-start gap-3 text-sm">
            <input
              type="checkbox"
              className="check mt-1"
              checked={draft.settings?.allowStudentRegistration ?? true}
              onChange={(e) => setDraft((d) => ({ ...d, settings: { ...d.settings, allowStudentRegistration: e.target.checked } }))}
            />
            <span>Allow students to register themselves</span>
          </label>
          <label className="flex items-start gap-3 text-sm">
            <input
              type="checkbox"
              className="check mt-1"
              checked={draft.settings?.requireEmailVerification ?? true}
              onChange={(e) => setDraft((d) => ({ ...d, settings: { ...d.settings, requireEmailVerification: e.target.checked } }))}
            />
            <span>Require email verification before sign-in</span>
          </label>
          <label className="flex items-start gap-3 text-sm">
            <input
              type="checkbox"
              className="check mt-1"
              checked={draft.settings?.allowPublicOpportunities ?? false}
              onChange={(e) => setDraft((d) => ({ ...d, settings: { ...d.settings, allowPublicOpportunities: e.target.checked } }))}
            />
            <span>Publish some opportunities publicly (careers page)</span>
          </label>
          <button
            className="btn btn-primary justify-self-start"
            disabled={save.isPending}
            onClick={() => doSave('Data settings', { settings: draft.settings })}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </section>

        <section className="card card-pad grid gap-4">
          <h2 className="h-section">Branding</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="grid gap-1.5">
              <span className="label">Display name</span>
              <input
                className="input"
                value={draft.name ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              />
            </label>
            <label className="grid gap-1.5">
              <span className="label">Primary colour</span>
              <input
                className="input"
                type="color"
                value={draft.primaryColor ?? '#1F40AF'}
                onChange={(e) => setDraft((d) => ({ ...d, primaryColor: e.target.value }))}
                style={{ padding: 4, height: 40 }}
              />
            </label>
          </div>
          <button
            className="btn btn-primary justify-self-start"
            disabled={save.isPending}
            onClick={() => doSave('Branding', { name: draft.name, primaryColor: draft.primaryColor })}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </section>
      </AsyncBoundary>
    </div>
  );
}
